import { gql, QueryResult } from '@apollo/client'
import { RouteComponentProps } from '@reach/router'
import * as React from 'react'
import styled from 'styled-components'
import Checkbox from '../../../common/src/components/Checkbox'
import { Link } from '../../../common/src/components/Link'
import Table from '../../../common/src/components/Table'
import { H1, Spacer, H2, BodyText, ColoredSpan, H3 } from '../../../common/src/components/core'
import { handleError } from '../../../common/src/data/errors'
import { secsAfter } from '../../../common/src/date'
import memoize from '../../../common/src/memoize'
import {
  WarnLevel,
  FetchOnboardingTasksQuery,
  FetchStatusBoardQuery,
  StatusMonitorType,
  TaskType,
  LeasePropertyType,
  OnboardingStatus,
  PmType,
} from '../../../common/src/types.gen'
import { Fragments } from '../graphql/fragments'
import { updateTask } from '../graphql/mutations'
import { PollingQuery } from './components/PollingQuery'

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export default function StatusPage(props: RouteComponentProps) {
  return (
    <StatusWrap>
      <H1>Status Board</H1>
      <Spacer height={32} />
      <H2>Errors</H2>
      <Spacer height={16} />
      <ErrorList />
      <Spacer height={32} />
      <H2>Firms</H2>
      <Spacer height={16} />
      <StatusBoard />
      <Spacer height={32} />
      <H2>Onboarding</H2>
      <Spacer height={16} />
      <OnboardingTable />
    </StatusWrap>
  )
}

const StatusWrap = styled.div`
  max-width: 1152px;
  width: 1152px;
  margin: 0 auto;
  box-sizing: border-box;
  padding: 32px;
  overflow: hidden;
`

const fetchStatusBoardQuery = gql`
  query FetchStatusBoard {
    statusBoard {
      firm {
        id
        firmName
      }
      warnLevel
      statusMonitorType
      lastUpdated
    }
  }
`

const fetchOnboardingTasksQuery = gql`
  query FetchOnboardingTasks {
    onboardingTasks {
      id
      ...LeasingTask
      onboardingInfo {
        propertyDetailsStatus
        bookedTrainingStatus
        confirmedSourcesStatus
        confirmedMarketLaunchDateStatus
        provisionedEmailStatus
        enabledPmStatus
        addedLisaDynastyStatus
      }
      pmItemKeys {
        pmType
      }
    }
  }
  ${Fragments.leasingTask}
  ${Fragments.contact}
  ${Fragments.address}
  ${Fragments.firm}
`

interface RowType {
  firm: {
    id: number
    firmName: string
  }
  calendarPoll?: Status
  lisaCalendarPoll?: Status
  agentStatusChange?: Status
  pmPropertyAudit?: Status
}

interface Status {
  lastUpdated?: string
  warnLevel?: WarnLevel
}

type OnboardingTask = FetchOnboardingTasksQuery['onboardingTasks'][0]

function ErrorList() {
  return (
    <Link href="https://sentry.io/organizations/dynasty/issues/?project=5319827">
      <BodyText>See Ops Sentry Board</BodyText>
    </Link>
  )
}

class StatusBoardImpl extends React.PureComponent<QueryResult<FetchStatusBoardQuery>> {
  render() {
    const { data } = this.props

    if (data == null) {
      return null
    }

    const { statusBoard } = data

    // sort alphabetically by firm name
    const sorted = statusBoard.slice().sort((a, b) => a.firm.firmName.localeCompare(b.firm.firmName))

    const rows = [] as RowType[]
    for (const s of sorted || []) {
      let lastRow = rows[rows.length - 1]
      if (lastRow == null || lastRow.firm.id !== s.firm.id) {
        lastRow = { firm: s.firm }
        rows.push(lastRow)
      }
      const status = { lastUpdated: s.lastUpdated || undefined, warnLevel: s.warnLevel }
      switch (s.statusMonitorType) {
        case StatusMonitorType.LISA_CALENDAR_POLL:
          lastRow.lisaCalendarPoll = status
          break
        case StatusMonitorType.CALENDAR_POLL:
          lastRow.calendarPoll = status
          break
        case StatusMonitorType.PM_PROPERTY_AUDIT:
          lastRow.pmPropertyAudit = status
          break
      }
    }

    if (rows.length === 0) {
      return <BodyText>Loading...</BodyText>
    }

    return <Table keyFn={this.rowKeyFn} rows={rows} columns={this.cols} headers={this.headers} widths={this.widths} />
  }

  private rowKeyFn = (row: RowType) => row.firm.id

  private headers = ['Firm', 'LisaCal Poll', 'Cal Poll', 'Property Sync']

  private cols = [
    (row: RowType) => row.firm.firmName,
    (row: RowType) => <StatusCell status={row.lisaCalendarPoll} />,
    (row: RowType) => <StatusCell status={row.calendarPoll} />,
    (row: RowType) => <StatusCell status={row.pmPropertyAudit} />,
  ]

  private widths = ['128px', '128px', '128px', '128px', null]
}

function StatusCell(props: { status?: Status }) {
  const { status } = props
  const now = new Date()
  if (status == null || status.lastUpdated == null || isNaN(secsAfter(now, status.lastUpdated))) {
    return <ColoredSpan color={'midway'}>{'never updated'}</ColoredSpan>
  }
  const lastUpdated = status.lastUpdated
  const secsAgo = secsAfter(now, lastUpdated)
  const agoString =
    secsAgo > 60 * 60 * 24
      ? Math.floor(secsAgo / (60 * 60 * 24)) + ' days ago'
      : secsAgo > 60 * 60
      ? Math.floor(secsAgo / (60 * 60)) + ' hours ago'
      : secsAgo > 60
      ? Math.floor(secsAgo / 60) + ' mins ago'
      : Math.floor(secsAgo) + ' secs ago'
  const color = status.warnLevel === 'WARN' ? 'lemon' : status.warnLevel === 'ERROR' ? 'coral' : 'midway'
  return <ColoredSpan color={color}>{agoString}</ColoredSpan>
}

class OnboardingTableImpl extends React.PureComponent<QueryResult<FetchOnboardingTasksQuery>> {
  private readonly clickHandlers: Record<string, () => void> = {}

  render() {
    const { data } = this.props

    if (data == null) {
      return null
    }

    const { onboardingTasks } = data
    if (onboardingTasks == null) {
      return <BodyText>Loading...</BodyText>
    }
    if (onboardingTasks.length === 0) {
      return <BodyText>No onboarding tasks</BodyText>
    }

    const leasingFirmTasks = onboardingTasks
      .filter(task => task.taskType === TaskType.LEASING_FIRM)
      .reduce((acc: { [key: number]: OnboardingTask }, task: OnboardingTask) => {
        acc[task.firm.id] = task
        return acc
      }, {} as { [key: number]: OnboardingTask })

    const nonSfrTasks = onboardingTasks.filter(
      task =>
        task.taskType === TaskType.LEASING_PROPERTY &&
        (task.leasingInfo == null || task.leasingInfo.propertyType !== LeasePropertyType.SFR)
    )

    const sfrTasks = onboardingTasks
      .filter(task => task.leasingInfo && task.leasingInfo.propertyType === LeasePropertyType.SFR)
      .sort(this.firmNameComparator)

    const combinedSfrTasks = this.combineSfrTasks(sfrTasks, leasingFirmTasks)

    let leasingPropertyTasks = combinedSfrTasks.concat(nonSfrTasks)

    leasingPropertyTasks = this.setItStatuses(leasingPropertyTasks, leasingFirmTasks)

    const groupedTasks = this.groupByFirm(leasingPropertyTasks)

    return Object.keys(groupedTasks).map(firmId => (
      <span key={firmId}>
        <H3>{groupedTasks[+firmId][0].firm.firmName}</H3>
        <Spacer height={8} />
        <Table keyFn={this.rowKeyFn} rows={groupedTasks[+firmId]} headers={this.headers} columns={this.cols} />
        <Spacer height={32} />
      </span>
    ))
  }

  private cols = [
    (row: OnboardingTask) => row.displayName,
    (row: OnboardingTask) => this.statusToEmoji(row.onboardingInfo.provisionedEmailStatus),
    (row: OnboardingTask) => this.statusToEmoji(row.onboardingInfo.enabledPmStatus),
    (row: OnboardingTask) => this.statusToEmoji(row.onboardingInfo.addedLisaDynastyStatus),
    (row: OnboardingTask) => this.statusToEmoji(row.onboardingInfo.propertyDetailsStatus),
    (row: OnboardingTask) =>
      this.checkBoxForStatus(row, 'bookedTrainingStatus', row.onboardingInfo.bookedTrainingStatus),
    (row: OnboardingTask) =>
      this.checkBoxForStatus(row, 'confirmedSourcesStatus', row.onboardingInfo.confirmedSourcesStatus),
    (row: OnboardingTask) =>
      this.checkBoxForStatus(
        row,
        'confirmedMarketLaunchDateStatus',
        row.onboardingInfo.confirmedMarketLaunchDateStatus
      ),
    (row: OnboardingTask) => row.launchDate,
  ]

  private headers = [
    'Task',
    'IT: Email',
    'IT: Enable PM',
    'IT: Add Agent',
    'Ops: Prop Info',
    'Ops: Training',
    'Mrktng: Sources',
    'Mrktng: Date',
    'Launch Date',
  ]

  private statusToEmoji = (status: OnboardingStatus) => (status === OnboardingStatus.COMPLETED ? '✅' : '❌')

  private checkBoxForStatus = (task: OnboardingTask, key: string, status: OnboardingStatus) => {
    const newStatus = status === OnboardingStatus.COMPLETED ? OnboardingStatus.REQUESTED : OnboardingStatus.COMPLETED
    const memoKey = `${task.id}-${key}`

    const checkBoxOnClick = memoize(this.clickHandlers, memoKey, () => () => {
      void updateTask(task, key, newStatus, true)
        .then(() => this.props.refetch()) // refetch to update checkbox
        .catch(handleError)
    })

    return (
      <Checkbox check={status === OnboardingStatus.COMPLETED ? 'checked' : 'unchecked'} onClick={checkBoxOnClick} />
    )
  }

  private rowKeyFn = (row: OnboardingTask) => row.id

  // Groups sfr tasks into a single row and sets its propertyDetailsStatus to that its firm.
  private combineSfrTasks = (sfrTasks: OnboardingTask[], leasingFirmTasks: { [key: number]: OnboardingTask }) => {
    const ret = [] as OnboardingTask[]
    const sfrCount = {} as { [key: number]: number }
    for (const t of sfrTasks) {
      // make copies of t and t.onboardingInfo to avoid getting an error when assigning a new value to propertyDetailsStatus
      const task = { ...t }
      const onboardingInfo = { ...t.onboardingInfo }
      sfrCount[t.firm.id] = (sfrCount[t.firm.id] || 0) + 1
      if (ret.length === 0 || ret[ret.length - 1].firm.firmName !== t.firm.firmName) {
        // set ops: property info to the firm's onboarding status
        if (leasingFirmTasks[t.firm.id]) {
          onboardingInfo.propertyDetailsStatus = leasingFirmTasks[t.firm.id].onboardingInfo.propertyDetailsStatus
          task.onboardingInfo = onboardingInfo
        }
        ret.push(task)
      }
      ret[ret.length - 1].displayName = `${sfrCount[t.firm.id]} SFR tasks`
    }
    return ret
  }

  private firmNameComparator = (a: OnboardingTask, b: OnboardingTask) =>
    a.firm.firmName < b.firm.firmName ? -1 : a.firm.firmName > b.firm.firmName ? 1 : 0

  // Onboarding Statuses for IT steps live in the firm task.
  private setItStatuses = (
    leasingPropertyTasks: OnboardingTask[],
    leasingFirmTasks: { [key: number]: OnboardingTask }
  ) =>
    leasingPropertyTasks.map(t => {
      const firmTask = leasingFirmTasks[t.firm.id]
      const { provisionedEmailStatus, enabledPmStatus, addedLisaDynastyStatus } = firmTask.onboardingInfo
      const isApm = t.pmItemKeys[0].pmType === PmType.APPFOLIO
      const onboardingInfo = { ...t.onboardingInfo }
      Object.assign(onboardingInfo, {
        provisionedEmailStatus,
        enabledPmStatus: isApm ? OnboardingStatus.COMPLETED : enabledPmStatus,
        addedLisaDynastyStatus: isApm ? OnboardingStatus.COMPLETED : addedLisaDynastyStatus,
      })
      const task: FetchOnboardingTasksQuery['onboardingTasks'][0] = { ...t }
      task.onboardingInfo = onboardingInfo
      return task
    })

  private groupByFirm = (leasingPropertyTasks: OnboardingTask[]) =>
    leasingPropertyTasks.reduce((acc: { [key: number]: OnboardingTask[] }, task: OnboardingTask) => {
      acc[task.firm.id] = acc[task.firm.id] || []
      acc[task.firm.id].push(task)
      return acc
    }, {} as { [key: number]: OnboardingTask[] })
}

function StatusBoard() {
  return (
    <PollingQuery query={fetchStatusBoardQuery}>
      {(res: QueryResult<FetchStatusBoardQuery>) => <StatusBoardImpl {...res} />}
    </PollingQuery>
  )
}

function OnboardingTable() {
  return (
    <PollingQuery query={fetchOnboardingTasksQuery}>
      {(res: QueryResult<FetchOnboardingTasksQuery>) => <OnboardingTableImpl {...res} />}
    </PollingQuery>
  )
}
